import { useEffect, useState } from "react";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { Bell, Mail } from "lucide-react";
import { SmsSettingsCard } from "@/components/sms/SmsSettingsCard";

/**
 * RN-G.3 · Merchant notification settings.
 * Currently exposes the daily cashback recap email toggle. This is the
 * source-of-truth control; the Overview page renders a compact mirror.
 */
export default function MerchantSettings() {
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    api.get("/merchant/cashback-recap/preview")
      .then((r) => setPreview(r.data))
      .catch(() => setError("Could not load your recap preview."));
  };
  useEffect(load, []);

  const toggle = async (value) => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.patch("/merchant/cashback-recap/settings", {
        cashback_recap_email: value,
      });
      setPreview((p) => (p ? { ...p, settings: r.data.settings } : p));
      setSavedAt(new Date().toLocaleTimeString());
    } catch {
      setError("Could not update preference.");
    } finally {
      setSaving(false);
    }
  };

  const enabled = preview?.settings?.cashback_recap_email !== false;
  const t = preview?.totals;
  const top = preview?.top_offers || [];

  return (
    <div data-testid="merchant-settings-page" className="max-w-3xl">
      <div className="pf-chip pf-chip-cyan"><Bell className="w-3 h-3" /> Settings</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Notifications</h2>
      <p className="text-sm text-slate-600 mt-1">Choose which emails and alerts we send to your merchant account.</p>

      <SmsSettingsCard />

      <div className="pf-card mt-6" data-testid="settings-cashback-recap-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-medium tracking-tight text-slate-900">Daily cashback recap</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-md">
                A digest of the last 24 hours of cashback issued, revenue routed via Cyannect, top-engaged
                offers and payouts settled to shoppers. Delivered every day at 13:00 UTC.
              </p>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              disabled={saving || !preview}
              onChange={(e) => toggle(e.target.checked)}
              data-testid="settings-cashback-recap-toggle"
              className="w-5 h-5 rounded"
            />
            <span className="text-sm font-semibold text-slate-700">{enabled ? "On" : "Off"}</span>
          </label>
        </div>
        {savedAt && <p className="text-xs text-emerald-600 mt-3" data-testid="settings-saved">Saved · {savedAt}</p>}
        {error && <p className="text-xs text-rose-600 mt-3" data-testid="settings-error">{error}</p>}
      </div>

      {t && (
        <div className="pf-card mt-6" data-testid="settings-recap-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-medium tracking-tight text-slate-900">Today's preview</h3>
            <span className="text-xs text-slate-500">last 24 hours</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-cyan-50">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Cashback issued</div>
              <div className="font-mono text-2xl mt-1">{cad(t.cashback_issued)}</div>
              <div className="text-xs text-slate-500">{t.cashback_count} shoppers</div>
            </div>
            <div className="p-3 rounded-xl bg-blue-50">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Revenue routed</div>
              <div className="font-mono text-2xl mt-1">{cad(t.revenue_routed)}</div>
              <div className="text-xs text-slate-500">{t.cashback_ratio_pct}% cashback ratio</div>
            </div>
            <div className="p-3 rounded-xl bg-blue-50">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Payouts settled</div>
              <div className="font-mono text-2xl mt-1">{cad(t.payouts_settled)}</div>
              <div className="text-xs text-slate-500">{t.payouts_count} e-transfers</div>
            </div>
            <div className="p-3 rounded-xl bg-cyan-50">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Top offers</div>
              <div className="font-mono text-2xl mt-1">{top.length}</div>
              <div className="text-xs text-slate-500">ranked by engagement</div>
            </div>
          </div>
          {top.length > 0 && (
            <ul className="mt-4 divide-y divide-white/60">
              {top.map((o) => (
                <li key={o.campaign_id} className="py-2 flex justify-between text-sm" data-testid={`settings-top-offer-${o.campaign_id}`}>
                  <span className="text-slate-700">{o.campaign_name}</span>
                  <span className="font-mono text-slate-500">{o.engagement}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
